import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AiAssistButton } from "@/components/AiAssistButton";
import { MicButton } from "@/components/MicButton";
import { Loader2, Plus, Star, Trash2, User, Package, Users } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";

const CREATOR_FIELDS = [
    { key: "name_title", label: "Name / Professional Title", helper: "How you introduce yourself. e.g. 'Danielle, Positioning Consultant'.", type: "input" },
    { key: "communication_style", label: "Communication Style", helper: "Your default on-camera tone.", type: "input", options: [
        "Calm and grounded", "Direct and no-BS", "Assertive and challenging",
        "Conversational and warm", "Analytical and instructional",
    ] },
    { key: "bio_story", label: "Bio / Origin Story", helper: "Your journey — what got you here and why you do this work.", type: "textarea" },
    { key: "credibility_wins", label: "Credibility & Client Wins", helper: "Credentials, years, notable results, client outcomes.", type: "textarea" },
    { key: "signature_phrase", label: "Signature Phrases", helper: "Phrases you find yourself repeating to clients. Add as many as you like — one per line works well.", type: "textarea" },
    { key: "brand_words", label: "Brand Words & Language", helper: "Distinctive words, coined terms, framework names, or vocabulary that belongs to your brand voice. e.g. 'Obvious choice', 'Positioning gap', 'Un-Cloned'. Separate with commas or new lines.", type: "textarea" },
    { key: "contrarian_belief", label: "Contrarian Belief About Your Industry", helper: "The take your peers are still afraid to say.", type: "textarea" },
    { key: "industry_gets_wrong", label: "What Your Industry Gets Wrong", helper: "The common mistake — and how you solve it.", type: "textarea" },
    { key: "why_message_works_now", label: "Why Your Message Works Now", helper: "The timing / market / cultural moment.", type: "textarea" },
    { key: "default_cta", label: "Default CTA Style", helper: "Your usual ask — DM a keyword, click a link, apply, opt-in, etc.", type: "textarea" },
];

const AVATAR_FIELDS = [
    { key: "avatar_type", label: "Type", helper: "Pick one. Audience = who they are demo/psycho. Situation = the moment they're stuck in.", type: "choice", options: ["Audience", "Situation", "Both"] },
    { key: "who_for", label: "Who is this Avatar FOR?", helper: "Describe them concretely — role, life stage, or the situation they're in.", type: "textarea" },
    { key: "who_not_for", label: "Who is this NOT for?", helper: "Naming the exclusion sharpens your positioning.", type: "textarea" },
    { key: "their_pain", label: "Their #1 Pain / Struggle", helper: "The problem keeping them up at night.", type: "textarea" },
    { key: "desired_outcome", label: "Desired Outcome", helper: "What they're actively trying to achieve.", type: "textarea" },
    { key: "tried_before", label: "What They've Already Tried", helper: "Attempts that didn't work — this is where authority lives.", type: "textarea" },
    { key: "objections", label: "Objections / What Blocks Them", helper: "Why they hesitate to buy or trust.", type: "textarea" },
    { key: "their_language", label: "Their Language", helper: "Phrases they'd naturally say — captures their voice.", type: "textarea" },
];

const Section = ({ icon: Icon, title, description, children, action }) => (
    <div className="un-card p-8 mb-8">
        <div className="flex items-start justify-between gap-4 mb-6">
            <div>
                <div className="flex items-center gap-3 mb-2">
                    <Icon className="w-5 h-5 text-un-berry" />
                    <h2 className="un-h2 text-2xl">{title}</h2>
                </div>
                <p className="text-un-onyx/60 max-w-2xl">{description}</p>
            </div>
            {action}
        </div>
        {children}
    </div>
);

const FieldEditor = ({ field, value, onChange, allFields }) => {
    const showAi = ["bio_story", "credibility_wins", "signature_phrase", "brand_words", "contrarian_belief",
                    "industry_gets_wrong", "why_message_works_now", "default_cta",
                    "who_for", "who_not_for", "their_pain", "desired_outcome",
                    "tried_before", "objections", "their_language"].includes(field.key);
    return (
        <div className="mb-7">
            <label className="block mb-2 text-base font-bold text-un-onyx" data-testid={`profile-field-label-${field.key}`}>{field.label}</label>
            {field.helper && <p className="text-sm text-un-onyx/70 mb-3 leading-relaxed">{field.helper}</p>}
            {field.type === "input" && (
                <>
                    <input
                        type="text"
                        value={value || ""}
                        onChange={(e) => onChange(e.target.value)}
                        className="un-input"
                        data-testid={`profile-field-${field.key}`}
                    />
                    {field.options && (
                        <div className="mt-2 flex flex-wrap gap-2">
                            {field.options.map((opt) => (
                                <button
                                    key={opt}
                                    type="button"
                                    onClick={() => onChange(opt)}
                                    className={`text-xs border-2 border-un-onyx px-3 py-1 ${value === opt ? "bg-un-berry text-white border-un-berry" : "bg-white hover:bg-un-onyx hover:text-white"}`}
                                >
                                    {opt}
                                </button>
                            ))}
                        </div>
                    )}
                </>
            )}
            {field.type === "textarea" && (
                <textarea
                    rows={3}
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    className="un-input"
                    data-testid={`profile-field-${field.key}`}
                />
            )}
            {field.type === "choice" && (
                <div className="flex flex-wrap gap-2">
                    {field.options.map((opt) => (
                        <button
                            key={opt}
                            type="button"
                            onClick={() => onChange(opt)}
                            className={`border-2 border-un-onyx px-4 py-2 font-semibold ${value === opt ? "bg-un-berry text-white border-un-berry" : "bg-white hover:bg-un-onyx hover:text-white"}`}
                            data-testid={`profile-choice-${field.key}-${opt}`}
                        >
                            {opt}
                        </button>
                    ))}
                </div>
            )}
            {(field.type === "textarea" || field.type === "input") && (
                <div className="mt-2 flex justify-end gap-2 flex-wrap">
                    {showAi && (
                        <AiAssistButton
                            fieldKey={field.key}
                            fieldLabel={field.label}
                            currentFields={allFields}
                            currentValue={value || ""}
                            onApply={(t) => onChange(t)}
                        />
                    )}
                    <MicButton onAppend={(t) => onChange(((value || "") + (value ? " " : "") + t).trim())} label="Dictate" />
                </div>
            )}
        </div>
    );
};

const Profile = () => {
    const [search] = useSearchParams();
    const returnTo = search.get("return");
    const [creator, setCreator] = useState({});
    const [offers, setOffers] = useState([]);
    const [avatars, setAvatars] = useState([]);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [editingOffer, setEditingOffer] = useState(null);
    const [editingAvatar, setEditingAvatar] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const [{ data: cp }, { data: ofs }, { data: avs }] = await Promise.all([
                    api.get("/me/creator-profile"),
                    api.get("/me/offers"),
                    api.get("/me/avatars"),
                ]);
                setCreator(cp?.fields || {});
                setOffers(ofs || []);
                setAvatars(avs || []);
            } catch {
                toast.error("Failed to load profile");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const saveCreator = async () => {
        setSaving(true);
        try {
            await api.put("/me/creator-profile", { fields: creator });
            toast.success("About You saved");
        } catch {
            toast.error("Save failed");
        } finally {
            setSaving(false);
        }
    };

    const upsertOffer = async (draft) => {
        try {
            const payload = {
                name: draft.name,
                summary: draft.summary,
                whats_included: draft.whats_included,
                investment: draft.investment,
                how_to_sign_up: draft.how_to_sign_up,
            };
            if (draft.id) {
                const { data } = await api.patch(`/me/offers/${draft.id}`, payload);
                setOffers(offers.map((o) => (o.id === draft.id ? data : o)));
            } else {
                const { data } = await api.post("/me/offers", payload);
                setOffers([...offers, data]);
            }
            setEditingOffer(null);
            toast.success("Offer saved");
        } catch {
            toast.error("Save failed");
        }
    };

    const deleteOffer = async (id) => {
        if (!confirm("Delete this offer?")) return;
        try {
            await api.delete(`/me/offers/${id}`);
            setOffers(offers.filter((o) => o.id !== id));
            toast.success("Deleted");
        } catch {
            toast.error("Delete failed");
        }
    };

    const upsertAvatar = async (draft) => {
        try {
            const payload = { name: draft.name, fields: draft.fields, is_default: draft.is_default };
            if (draft.id) {
                const { data } = await api.patch(`/me/avatars/${draft.id}`, payload);
                setAvatars(avatars.map((a) => (a.id === draft.id ? data : a)));
            } else {
                const { data } = await api.post("/me/avatars", payload);
                setAvatars([...avatars, data]);
            }
            setEditingAvatar(null);
            toast.success("Avatar saved");
        } catch {
            toast.error("Save failed");
        }
    };

    const deleteAvatar = async (id) => {
        if (!confirm("Delete this Avatar?")) return;
        try {
            await api.delete(`/me/avatars/${id}`);
            setAvatars(avatars.filter((a) => a.id !== id));
            toast.success("Deleted");
        } catch {
            toast.error("Delete failed");
        }
    };

    if (loading) {
        return <div className="max-w-4xl mx-auto p-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;
    }

    return (
        <div className="max-w-5xl mx-auto p-6 md:p-10">
            {returnTo && (
                <Link
                    to={returnTo}
                    className="inline-flex items-center gap-2 mb-6 text-sm un-btn-outline"
                    data-testid="return-to-brief"
                >
                    ← Return to your video brief
                </Link>
            )}
            <div className="mb-10">
                <div className="un-label text-un-grape mb-2">Profile</div>
                <h1 className="un-h1 text-4xl md:text-5xl">Your Creator Studio.</h1>
                <p className="text-base md:text-lg text-un-onyx/70 mt-3 max-w-2xl leading-relaxed">
                    Set up your identity once. Build a library of offers and avatars. Every video brief gets stronger the more you fill in.
                </p>
            </div>

            {/* Section 1 — About You */}
            <Section
                icon={User}
                title="About You"
                description="The base identity of the creator on camera. This never changes video-to-video."
                action={
                    <button onClick={saveCreator} disabled={saving} className="un-btn-primary text-sm flex items-center gap-2" data-testid="save-creator-btn">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save About You
                    </button>
                }
            >
                {CREATOR_FIELDS.map((f) => (
                    <FieldEditor
                        key={f.key}
                        field={f}
                        value={creator[f.key]}
                        onChange={(v) => setCreator({ ...creator, [f.key]: v })}
                        allFields={creator}
                    />
                ))}
            </Section>

            {/* Section 2 — Offers */}
            <Section
                icon={Package}
                title={<>5 Must-Haves<sup className="text-xs">™</sup> Offers Framework</>}
                description="Every offer you add follows the same five-part framework. If a video is just authority content with no offer, leave the offer empty when starting the brief."
                action={
                    <button
                        onClick={() => setEditingOffer({ name: "", summary: "", whats_included: "", investment: "", how_to_sign_up: "" })}
                        className="un-btn-primary text-sm flex items-center gap-2"
                        data-testid="new-offer-btn"
                    >
                        <Plus className="w-4 h-4" /> New Offer
                    </button>
                }
            >
                {offers.length === 0 && !editingOffer && (
                    <p className="text-un-onyx/50 italic">No offers yet. Add one to attach it to a video brief.</p>
                )}
                <div className="space-y-4">
                    {offers.map((o) => (
                        <div key={o.id} className="border-2 border-un-onyx p-6 flex items-start justify-between gap-4" data-testid={`offer-${o.id}`}>
                            <div className="min-w-0 flex-1">
                                <div className="un-h2 text-lg mb-1">{o.name}</div>
                                {o.investment && <div className="text-sm text-un-grape font-bold mb-2">{o.investment}</div>}
                                {(o.summary || o.description) && <p className="text-base text-un-onyx/85 mb-2 leading-relaxed">{o.summary || o.description}</p>}
                                {o.whats_included && <p className="text-sm text-un-onyx/70 mt-2 leading-relaxed"><b>Includes:</b> {o.whats_included}</p>}
                                {o.how_to_sign_up && <p className="text-sm text-un-onyx/70 mt-2 leading-relaxed"><b>Sign up:</b> {o.how_to_sign_up}</p>}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                <button onClick={() => setEditingOffer(o)} className="un-btn-outline text-sm" data-testid={`edit-offer-${o.id}`}>Edit</button>
                                <button onClick={() => deleteOffer(o.id)} className="un-btn-ghost !px-2 text-un-onyx/40 hover:!text-un-berry"><Trash2 className="w-4 h-4" /></button>
                            </div>
                        </div>
                    ))}
                </div>
                {editingOffer && (
                    <OfferEditor draft={editingOffer} onCancel={() => setEditingOffer(null)} onSave={upsertOffer} />
                )}
            </Section>

            {/* Section 3 — Avatars */}
            <Section
                icon={Users}
                title="Avatars — Target Audiences & Situations"
                description="Your library of who you speak to. Each Avatar is either a target audience (a demo/psychographic) or a target situation (a moment they're stuck in). Attach one when starting a video brief."
                action={
                    <button
                        onClick={() => setEditingAvatar({ name: "", fields: { avatar_type: "Situation" }, is_default: avatars.length === 0 })}
                        className="un-btn-primary text-sm flex items-center gap-2"
                        data-testid="new-avatar-btn"
                    >
                        <Plus className="w-4 h-4" /> New Avatar
                    </button>
                }
            >
                {avatars.length === 0 && !editingAvatar && (
                    <p className="text-un-onyx/50 italic">No avatars yet. Add one to be able to target a specific audience or situation in your videos.</p>
                )}
                <div className="space-y-4">
                    {avatars.map((a) => (
                        <div key={a.id} className="border-2 border-un-onyx p-5 flex items-start justify-between gap-4" data-testid={`avatar-${a.id}`}>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <div className="un-h2 text-lg">{a.name}</div>
                                    {a.is_default && <span className="un-pill un-pill-lime whitespace-nowrap">Default</span>}
                                    {a.fields?.avatar_type && <span className="un-pill un-pill-outline whitespace-nowrap">{a.fields.avatar_type}</span>}
                                </div>
                                {a.fields?.who_for && <p className="text-sm text-un-onyx/80 mb-1"><b>For:</b> {a.fields.who_for}</p>}
                                {a.fields?.their_pain && <p className="text-xs text-un-onyx/60"><b>Pain:</b> {a.fields.their_pain}</p>}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                <button onClick={() => setEditingAvatar(a)} className="un-btn-outline text-xs" data-testid={`edit-avatar-${a.id}`}>Edit</button>
                                <button onClick={() => deleteAvatar(a.id)} className="un-btn-ghost !px-2 text-un-onyx/40 hover:!text-un-berry"><Trash2 className="w-4 h-4" /></button>
                            </div>
                        </div>
                    ))}
                </div>
                {editingAvatar && (
                    <AvatarEditor
                        draft={editingAvatar}
                        creatorFields={creator}
                        onCancel={() => setEditingAvatar(null)}
                        onSave={upsertAvatar}
                    />
                )}
            </Section>
        </div>
    );
};

const OfferEditor = ({ draft, onCancel, onSave }) => {
    const [d, setD] = useState({
        name: draft.name || "",
        summary: draft.summary || draft.description || "",
        whats_included: draft.whats_included || "",
        investment: draft.investment || draft.price || "",
        how_to_sign_up: draft.how_to_sign_up || "",
        id: draft.id,
    });
    return (
        <div className="border-2 border-un-berry p-6 md:p-8 mt-4 bg-un-water/5" data-testid="offer-editor">
            <div className="mb-8 pb-6 border-b-2 border-un-berry/30">
                <div className="un-label text-un-grape mb-2">{d.id ? "Editing Offer" : "New Offer"}</div>
                <h3 className="un-h2 text-2xl md:text-3xl">
                    5 Must-Haves<sup className="text-base align-super">™</sup> Framework
                </h3>
                <p className="text-base text-un-onyx/70 mt-2 leading-relaxed">
                    Fill these out for every offer. This is the framework every generation uses when writing scripts about this offer.
                </p>
            </div>
            <div className="mb-7">
                <div className="text-lg font-bold text-un-berry mb-2">1 · Product Name</div>
                <p className="text-base text-un-onyx/70 mb-3 leading-relaxed">The name of the product or service.</p>
                <input type="text" value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} className="un-input text-base" data-testid="offer-name-input" />
            </div>
            <div className="mb-7">
                <div className="text-lg font-bold text-un-berry mb-2">2 · Offer Summary</div>
                <p className="text-base text-un-onyx/70 mb-3 leading-relaxed">A client-facing description — the short pitch people would see.</p>
                <textarea rows={3} value={d.summary} onChange={(e) => setD({ ...d, summary: e.target.value })} className="un-input text-base" data-testid="offer-summary-input" />
            </div>
            <div className="mb-7">
                <div className="text-lg font-bold text-un-berry mb-2">3 · Investment</div>
                <p className="text-base text-un-onyx/70 mb-3 leading-relaxed">The price. e.g. "$5,000 one-time" or "$497/month for 6 months".</p>
                <input type="text" value={d.investment} onChange={(e) => setD({ ...d, investment: e.target.value })} className="un-input text-base" data-testid="offer-investment-input" />
            </div>
            <div className="mb-7">
                <div className="text-lg font-bold text-un-berry mb-2">4 · What's Included</div>
                <p className="text-base text-un-onyx/70 mb-3 leading-relaxed">The deliverables. e.g. "12 months of 1:1 calls, a private Slack, a printed workbook shipped to your door."</p>
                <textarea rows={3} value={d.whats_included} onChange={(e) => setD({ ...d, whats_included: e.target.value })} className="un-input text-base" data-testid="offer-included-input" />
            </div>
            <div className="mb-8">
                <div className="text-lg font-bold text-un-berry mb-2">5 · How to Sign Up</div>
                <p className="text-base text-un-onyx/70 mb-3 leading-relaxed">The exact next step. Website, application link, DM keyword, meeting location — whatever kicks off enrollment.</p>
                <textarea rows={2} value={d.how_to_sign_up} onChange={(e) => setD({ ...d, how_to_sign_up: e.target.value })} className="un-input text-base" data-testid="offer-signup-input" />
            </div>
            <div className="flex gap-2 justify-end">
                <button onClick={onCancel} className="un-btn-outline text-sm">Cancel</button>
                <button onClick={() => onSave(d)} disabled={!d.name?.trim()} className="un-btn-primary text-sm" data-testid="offer-save-btn">Save Offer</button>
            </div>
        </div>
    );
};

const AvatarEditor = ({ draft, creatorFields, onCancel, onSave }) => {
    const [name, setName] = useState(draft.name || "");
    const [fields, setFields] = useState(draft.fields || {});
    const [isDefault, setIsDefault] = useState(!!draft.is_default);
    const allFields = useMemo(() => ({ ...creatorFields, ...fields }), [creatorFields, fields]);
    return (
        <div className="border-2 border-un-berry p-5 mt-4 bg-un-water/5" data-testid="avatar-editor">
            <div className="un-label text-un-berry mb-4">{draft.id ? "Edit Avatar" : "New Avatar"}</div>
            <div className="mb-4">
                <label className="un-label text-un-onyx block mb-2">Avatar Name</label>
                <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. 'Overlooked Female Coach' or 'Complacent Long-Timer'"
                    className="un-input"
                    data-testid="avatar-name-input"
                />
            </div>
            {AVATAR_FIELDS.map((f) => (
                <FieldEditor
                    key={f.key}
                    field={f}
                    value={fields[f.key]}
                    onChange={(v) => setFields({ ...fields, [f.key]: v })}
                    allFields={allFields}
                />
            ))}
            <label className="flex items-center gap-2 mb-4 cursor-pointer">
                <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} className="w-4 h-4" />
                <span className="text-sm">
                    <Star className="w-4 h-4 inline mr-1 text-un-berry" /> Set as default Avatar for new video briefs
                </span>
            </label>
            <div className="flex gap-2 justify-end">
                <button onClick={onCancel} className="un-btn-outline text-sm">Cancel</button>
                <button
                    onClick={() => onSave({ ...draft, name: name.trim(), fields, is_default: isDefault })}
                    disabled={!name.trim()}
                    className="un-btn-primary text-sm"
                    data-testid="avatar-save-btn"
                >
                    Save Avatar
                </button>
            </div>
        </div>
    );
};

export default Profile;
